import React from 'react';
import Icon from '../AppIcon';
import { FormSection } from '../orizon-ui/FormField';

// Elenco di nomi modificabile con il pulsante "+" (impianti e linee di una zona).
// Le righe già salvate non si cancellano: si disattivano. Le righe nuove si possono togliere.

/** Riga vuota da aggiungere (chiave locale per React, senza id finché non è salvata) */
export const nuovaRiga = () => ({
  chiave: crypto.randomUUID(),
  id: null,
  nome: '',
  attivo: true,
  originale: null,
  nuova: true,
});

/** Righe dell'elenco a partire dai record del database (già ordinati per "ordine") */
export const righeDaRecord = (records) => (records || []).map((r) => ({
  chiave: r.id,
  id: r.id,
  nome: r.nome ?? '',
  attivo: r.attivo,
  originale: { nome: r.nome, ordine: r.ordine, attivo: r.attivo },
  nuova: false,
}));

/** Le righe nuove lasciate vuote vengono ignorate */
export const righeDaSalvare = (righe) => righe.filter((r) => r.id || String(r.nome).trim() !== '');

/** Controlli prima di salvare: nome obbligatorio e nessun doppione (maiuscole e spazi ignorati). { [chiave]: messaggio } */
export const validaRighe = (righe) => {
  const errori = {};
  const visti = new Map();
  righeDaSalvare(righe).forEach((r) => {
    const nome = String(r.nome).trim();
    if (nome === '') {
      errori[r.chiave] = 'Obbligatorio';
      return;
    }
    const k = nome.toUpperCase();
    if (visti.has(k)) {
      errori[r.chiave] = 'Nome già inserito';
      errori[visti.get(k)] = errori[visti.get(k)] || 'Nome già inserito';
    } else {
      visti.set(k, r.chiave);
    }
  });
  return errori;
};

const ElencoNomi = ({
  titolo,
  icona,
  descrizione,
  segnaposto = 'Nome',
  aggiungiLabel = 'Aggiungi',
  vuotoLabel = 'Nessun elemento.',
  righe,
  onChange,
  errori = {},
  disabilitato = false,
}) => {
  const cambia = (chiave, patch) => onChange(righe.map((r) => (r.chiave === chiave ? { ...r, ...patch, nuova: false } : r)));
  const rimuovi = (chiave) => onChange(righe.filter((r) => r.chiave !== chiave));
  const aggiungi = () => onChange([...righe, nuovaRiga()]);

  return (
    <FormSection titolo={titolo} icona={icona}>
      {descrizione && (
        <p className="text-[10px] font-bold text-muted-foreground/50 -mt-1">{descrizione}</p>
      )}

      {righe.length === 0 && (
        <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40">{vuotoLabel}</p>
      )}

      <div className="flex flex-col gap-2">
        {righe.map((r, i) => {
          const errore = errori[r.chiave];
          return (
            <div key={r.chiave} className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="w-6 text-center text-[10px] font-black tabular-nums text-muted-foreground/40">{i + 1}</span>
                <input
                  type="text"
                  value={r.nome}
                  autoFocus={r.nuova}
                  disabled={disabilitato}
                  placeholder={segnaposto}
                  aria-label={`${titolo} ${i + 1}`}
                  aria-invalid={!!errore}
                  onChange={(e) => cambia(r.chiave, { nome: e.target.value })}
                  onKeyDown={(e) => {
                    // Invio = riga successiva (non invia il form)
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (String(r.nome).trim() !== '') aggiungi();
                    }
                  }}
                  className={`flex-1 min-w-0 rounded-2xl px-4 py-2.5 text-sm font-bold uppercase outline-none transition-all ${
                    !r.attivo ? 'opacity-50 line-through' : ''
                  } ${
                    errore
                      ? 'bg-red-500/5 border-2 border-red-500/40 focus:border-red-500/60 focus:ring-2 focus:ring-red-500/10'
                      : 'bg-muted/30 border border-border/30 focus:border-primary/50 focus:bg-background focus:ring-2 focus:ring-primary/10 hover:border-border/60'
                  }`}
                />
                {r.id ? (
                  <button
                    type="button"
                    disabled={disabilitato}
                    title={r.attivo ? 'Disattiva' : 'Riattiva'}
                    aria-label={r.attivo ? 'Disattiva' : 'Riattiva'}
                    onClick={() => cambia(r.chiave, { attivo: !r.attivo })}
                    className={`w-10 h-10 shrink-0 rounded-xl border flex items-center justify-center transition-all disabled:opacity-40 ${
                      r.attivo
                        ? 'bg-muted/30 border-border/40 text-muted-foreground hover:bg-amber-500/10 hover:text-amber-500 hover:border-amber-500/30'
                        : 'bg-muted/30 border-border/40 text-muted-foreground hover:bg-emerald-500/10 hover:text-emerald-500 hover:border-emerald-500/30'
                    }`}
                  >
                    <Icon name={r.attivo ? 'PowerOff' : 'Power'} size={15} />
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={disabilitato}
                    title="Togli"
                    aria-label="Togli"
                    onClick={() => rimuovi(r.chiave)}
                    className="w-10 h-10 shrink-0 rounded-xl border bg-muted/30 border-border/40 text-muted-foreground hover:bg-red-500/10 hover:text-red-500 hover:border-red-500/30 flex items-center justify-center transition-all disabled:opacity-40"
                  >
                    <Icon name="X" size={15} />
                  </button>
                )}
              </div>
              {errore && (
                <span role="alert" className="pl-8 text-[9px] font-black text-red-500 uppercase tracking-widest flex items-center gap-1">
                  <Icon name="AlertCircle" size={9} />
                  {errore}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        disabled={disabilitato}
        onClick={aggiungi}
        className="self-start flex items-center gap-2 px-4 py-2.5 rounded-2xl border border-dashed border-primary/40 text-primary hover:bg-primary/10 transition-all text-[10px] font-black uppercase tracking-[.2em] disabled:opacity-40 active:scale-95"
      >
        <Icon name="Plus" size={14} />
        {aggiungiLabel}
      </button>
    </FormSection>
  );
};

export default ElencoNomi;
